import React from 'react';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';

import { getCommonConfig } from 'app-components/utils/core';

import QAmountField from 'components/QAmountField';
import {
  matchCriteriaOptions,
  type ScheduledTransactionEditFormAccessors,
} from 'components/Dialogs/ScheduledTransactionEditDialog/types';

import { applyMatchCriteriaChange } from './matchCriteria';
import { useAmountFieldDraft } from './useAmountFieldDraft';
import { useAmountCardStyles } from './styles';

const MATCH_CRITERIA_ITEMS = [
  matchCriteriaOptions.AUTO_MATCH,
  matchCriteriaOptions.ANY_AMOUNT,
  matchCriteriaOptions.EXACT_AMOUNT,
  matchCriteriaOptions.LIMITED_RANGE,
] as const;

const MATCH_CRITERIA_INFO_VALUE = 'These options let you control the amount allowed to match with this series.';

export type AmountCardProps = {
  isIncome: boolean;
  amountError?: string;
  form: ScheduledTransactionEditFormAccessors;
  /** Overrides the default amount field — e.g. the "assign splits" panel button shown once a series has splits assigned. */
  amountSlot?: React.ReactNode;
  /** Whether this is a new (not-yet-saved) series; gates the `amountMatchVarianceFF` default. */
  create?: boolean;
  /** LaunchDarkly-configured default Limited Range variance percent for newly created series. */
  amountMatchVarianceFF?: number;
  /**
   * A manually added series (BillScoutSetup's in-flow "Add series" path)
   * starts with the form's internal zero amount for validation/serialization
   * purposes — present it as blank until the user enters a value instead
   * (per the Figma "Detail view" spec, node 2466:46152).
   */
  isNewManualSeries?: boolean;
  /** Called after a commit/change the host wants to treat as a form edit — e.g. BillScoutSetup's autosave debounce. */
  onFieldEdited?: () => void;
  /** DOM id for the amount field. Defaults to the ScheduledTransactionEditDialog's historical id. */
  amountFieldId?: string;
  /** DOM id for the match-criteria select. Defaults to the ScheduledTransactionEditDialog's historical id (asserted on in basicTab.test.tsx). */
  matchCriteriaId?: string;
  /** Shows the Limited Range dollar amount and "Learn More" info affordance — used by the full Scheduled Transaction Edit dialog, omitted in the simpler in-flow Series details pane. */
  showMatchCriteriaDetails?: boolean;
};

/**
 * Amount group shared by the Scheduled Transaction Edit dialog's Basic
 * Details card and BillScoutSetup's in-flow Series details pane: the
 * recurring amount (or a caller-provided `amountSlot`, e.g. the split-amount
 * panel button) and the match-criteria selector, side by side. Reads and
 * writes the enclosing scheduled transaction edit form directly. Rendered
 * inside the caller's own bordered card, so it owns no border of its own.
 */
export default function AmountCard({
  isIncome,
  amountError,
  form,
  amountSlot,
  create,
  amountMatchVarianceFF,
  isNewManualSeries = false,
  onFieldEdited,
  amountFieldId = 'schedtxn-amount',
  matchCriteriaId = 'series-match-criteria',
  showMatchCriteriaDetails = true,
}: AmountCardProps): React.JSX.Element {
  const { classes } = useAmountCardStyles();
  const { values } = form;
  const { transaction } = values;

  const { amountDraft, setAmountDraft, commitAmount } = useAmountFieldDraft({
    initialDraft: isNewManualSeries ? '' : (transaction.amount ?? ''),
    committedAmount: transaction.amount,
    revertToBlank: isNewManualSeries,
    onCommit: (amount) => {
      form.setters.setFieldValue('transaction.amount', amount);
      onFieldEdited?.();
    },
  });

  const variancePercent =
    create && amountMatchVarianceFF
      ? amountMatchVarianceFF
      : values.matchCriteria === matchCriteriaOptions.LIMITED_RANGE
        ? (values.matchAmountVariance ?? 15)
        : 15;
  const txnAmount = transaction.amount || 0;
  const rangeValue = txnAmount * (variancePercent / 100);
  const limitedRangeAmount = txnAmount
    ? `$${Math.abs(txnAmount - rangeValue).toFixed(2)} - $${Math.abs(txnAmount + rangeValue).toFixed(2)}`
    : '$0 - $0';

  return (
    <Box className={classes.row}>
      <Box className={classes.field}>
        {amountSlot ?? (
          <QAmountField
            name='transaction.amount'
            value={amountDraft}
            onChange={(event: { target: { value: string } }) =>
              setAmountDraft(event.target.value === '' ? '' : Number(event.target.value))
            }
            onBlur={commitAmount}
            expectedSign={isIncome ? 'positive' : 'negative'}
            editable
            id={amountFieldId}
            label='Amount'
            fullWidth
            textFieldVariant='filled'
            showColor='positive'
            placeholder={isNewManualSeries ? '' : '0.00'}
            error={Boolean(amountError)}
            helperText={amountError}
          />
        )}
      </Box>
      <Box className={classes.field}>
        <FormControl variant='filled' fullWidth>
          <InputLabel id={`${matchCriteriaId}-label`}>Match Criteria</InputLabel>
          <Select
            id={matchCriteriaId}
            labelId={`${matchCriteriaId}-label`}
            value={values.matchCriteria}
            onChange={(event) => {
              applyMatchCriteriaChange(event.target.value, form.setters, variancePercent);
              onFieldEdited?.();
            }}
            className={classes.matchSelect}
            renderValue={(value) => <Typography variant='bodyRegular'>{value}</Typography>}
          >
            {MATCH_CRITERIA_ITEMS.map((item, index) => (
              <MenuItem
                key={item}
                value={item}
                divider={item === matchCriteriaOptions.AUTO_MATCH || index === MATCH_CRITERIA_ITEMS.length - 1}
              >
                <Box>
                  <Typography variant='bodyRegular'>{item}</Typography>
                  {showMatchCriteriaDetails && item === matchCriteriaOptions.LIMITED_RANGE && (
                    <Typography variant='paragraphRegular' color='textSecondary'>
                      {limitedRangeAmount}
                    </Typography>
                  )}
                  {showMatchCriteriaDetails && item === matchCriteriaOptions.AUTO_MATCH && (
                    <Typography variant='paragraphRegular' color='textSecondary'>
                      Simplifi will optimize for you
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
            {showMatchCriteriaDetails && (
              <MenuItem key='info' value={MATCH_CRITERIA_INFO_VALUE}>
                <Box className={classes.innerCriteriaItem}>
                  <div className={classes.learnMore}>
                    <div>
                      <Typography variant='captionRegular' className={classes.learnMoreText}>
                        These options let you control the amount allowed to match with this series.
                      </Typography>
                      <br />
                      <Typography
                        variant='captionRegular'
                        id='learn_more'
                        color='primary'
                        style={{ textTransform: 'capitalize' }}
                        onClick={(event) => {
                          event.stopPropagation();
                          window.open(getCommonConfig()?.external?.recurring_transactions);
                        }}
                      >
                        Learn More {'>'}
                      </Typography>
                    </div>
                  </div>
                </Box>
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}
